import { Meeting } from '@/types/Meeting';
import { format } from 'date-fns';
import { MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MeetingCardProps {
  meeting: Meeting;
  currentUserEmail: string;
}

export function MeetingCard({ meeting, currentUserEmail }: MeetingCardProps) {
  const navigate = useNavigate();

  // Get action stats from backend
  const totalActions = meeting.actionStats?.total || 0;
  const completedActions = meeting.actionStats?.completed || 0;
  const completionPercent = totalActions > 0 
    ? Math.round((completedActions / totalActions) * 100) 
    : 0;

  // Determine if current user is owner
  const ownerEmail = typeof meeting.owner === 'string' ? meeting.owner : meeting.owner?.email;
  const isOwner = ownerEmail === currentUserEmail;

  return (
    <div
      onClick={() => navigate(`/app/meetings/${meeting.id}`)}
      className="glass-card rounded-3xl p-6 flex flex-col h-full relative group cursor-pointer"
    >
      {/* Top decorative line */}
      <div className="absolute top-0 left-6 right-6 h-[1px] bg-gradient-to-r from-transparent via-white to-transparent opacity-50" />

      {/* Card Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="bg-white/50 dark:bg-slate-700/50 border border-white/60 dark:border-slate-600/60 rounded-lg px-2.5 py-1 backdrop-blur-sm">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
            {format(new Date(meeting.date), 'MMM dd, yyyy, h:mm a')}
          </span>
        </div>
        <button className="text-slate-400 hover:text-indigo-500 transition-colors opacity-0 group-hover:opacity-100">
          <MoreVertical className="w-5 h-5" />
        </button>
      </div>

      {/* Title */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight group-hover:text-fluid-primary dark:group-hover:text-indigo-400 transition-colors">
          {meeting.title}
        </h3>
      </div>

      {/* Owner */}
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-fluid-primary to-fluid-accent flex items-center justify-center text-white font-bold text-xs border-2 border-white shadow-sm">
          {ownerEmail?.substring(0, 2).toUpperCase() || 'ME'}
        </div>
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase">
            Owner
          </span>
          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
            {isOwner ? 'Your meeting' : ownerEmail || 'Unknown'}
          </span>
        </div>
      </div>

      {/* Spacer to push progress section to bottom */}
      <div className="flex-1 min-h-0" />

      {/* Progress Section */}
      {totalActions > 0 && (
        <div className="bg-white/30 dark:bg-slate-800/30 rounded-xl p-3 border border-white/40 dark:border-slate-600/40">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1">
              <svg className="w-3 h-3 text-fluid-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Actions Completed
            </span>
            <span className="text-xs font-bold text-fluid-primary dark:text-indigo-400">
              {completionPercent}% ({completedActions}/{totalActions})
            </span>
          </div>
          <div className="w-full bg-white/70 dark:bg-slate-700/70 rounded-full h-1.5 overflow-hidden border border-white/40 dark:border-slate-600/40">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-fluid-primary to-fluid-accent transition-all duration-1000 ease-out" 
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
